import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  Flex,
  Grid,
  Heading,
  List,
  ListIcon,
  ListItem,
  TabPanel,
  TabPanels,
  Tag,
  Text,
} from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { SanityImage } from '~/components/sanity-image';
import { TabbedSection } from '~/components/tabbed-section';
import { trackEvent } from '~/features/analytics/track-event';
import { GenericPersonAvatar } from '~/features/enterprise/components/generic-person-avatar';
import {
  BriefCaseIcon,
  ChartUpIcon,
  CheckedIcon,
  ClipboardChecklistIcon,
  CommunicationsIcon,
  ComputerChipIcon,
  GearIcon,
  GraduationCapIcon,
  IndustryIcon,
  LightningIcon,
  MoneyIcon,
  SchoolIcon,
} from '~/features/experiments/ab_plp_redesign_2025/icons';
import type { SanityImageType } from '~/features/page-template/models/image';

/** Supports i18n-only anonymous attributions and CMS-backed real people. */
export type ClientSuccessPerson = {
  quoteKey: string;
  nameKey?: string;
  titleKey?: string;
  name?: string | null;
  title?: string | null;
  useGenericIcon?: boolean;
  image?: SanityImageType | null;
};

export type ClientSuccessStoriesSectionProps = {
  titleKey: string;
  subtitleKey: string;
  descriptionKey: string;
  industries: Array<{
    id: string;
    nameKey: string;
    theme: string;
    resourceUrl: string;
    stat: { value: string; labelKey: string };
    titleKey: string;
    descriptionKey: string;
    company: { name: string; logo: SanityImageType | null } | null;
    person: ClientSuccessPerson | null;
    howUdacityHelps: {
      titleKey: string;
      reasons: Array<{ iconId: string; textKey: string }>;
    };
  }>;
};

export type StaticClientSuccessStoriesSectionProps = ClientSuccessStoriesSectionProps;

export function ClientSuccessStoriesSection({
  titleKey,
  subtitleKey,
  descriptionKey,
  industries,
}: ClientSuccessStoriesSectionProps) {
  const { t } = useTranslation();
  function getIcon(id: string) {
    switch (id) {
      case 'financial-services':
        return SchoolIcon;
      case 'communications-and-tech':
        return CommunicationsIcon;
      case 'technology':
        return ComputerChipIcon;
      case 'education':
        return GraduationCapIcon;
      case 'products-and-manufacturing':
        return IndustryIcon;
      case 'resources-and-energy':
        return LightningIcon;
      case 'gear':
        return GearIcon;
      case 'dollar-sign':
        return MoneyIcon;
      case 'clipboard-checklist':
        return ClipboardChecklistIcon;
      default:
        return null;
    }
  }

  function getTheme(theme: string) {
    switch (theme) {
      case 'blue':
      default:
        return {
          primaryColor: 'blue.500',
          secondaryColor: 'blue.700',
          bgColor: 'blue.50',
          dividerColor: 'blue.100',
          backgroundAccentColor: 'blue.300',
          statbgGradient: 'linear(64deg, #031643 0%, #00298C 100%)',
        };
      case 'orange':
        return {
          primaryColor: 'orange.500',
          secondaryColor: 'orange.700',
          bgColor: 'orange.50',
          dividerColor: 'orange.100',
          backgroundAccentColor: 'orange.300',
          statbgGradient: 'linear(244.46deg, #C65100 0%, #602700 100%)',
        };
      case 'purple':
        return {
          primaryColor: 'purple.500',
          secondaryColor: 'purple.700',
          bgColor: 'purple.50',
          dividerColor: 'purple.100',
          backgroundAccentColor: 'purple.300',
          statbgGradient: 'linear(244.46deg, #6100B0 0%, #29004A 100%)',
        };
      case 'cyan':
        return {
          primaryColor: 'cyan.700',
          secondaryColor: 'cyan.700',
          bgColor: 'cyan.50',
          dividerColor: 'cyan.100',
          backgroundAccentColor: 'cyan.300',
          statbgGradient: 'linear(244.46deg, #007697 0%, #002631 100%)',
        };
    }
  }

  return (
    <TabbedSection
      headerIcon={<ChartUpIcon color='grey.500' height='18px' width='18px' />}
      headerSubtitle={t(subtitleKey) ?? ''}
      headerDescription={
        <Text color='grey.500' margin='0 auto' maxWidth='720px'>
          {t(descriptionKey)}
        </Text>
      }
      headerTitle={
        <Heading
          as='h2'
          color='grey.900'
          fontSize={{ base: '2.25rem', md: '3.375rem' }}
          fontWeight='600'
          letterSpacing='-2px'
          lineHeight='1.1'
        >
          {t(titleKey)}
        </Heading>
      }
      tabs={industries.map((industry) => {
        const Icon = getIcon(industry.id);
        const theme = getTheme(industry.theme);
        return {
          id: industry.id,
          label: t(industry.nameKey) ?? '',
          icon: Icon ? <Icon height='16px' width='16px' /> : undefined,
          selectedColor: theme?.primaryColor,
        };
      })}
      onTabChange={(index) => {
        const targetIndustry = industries[index];
        trackEvent({
          type: 'click',
          name: 'Enterprise CTA Clicked',
          label: 'client_success_clicked',
          value: t(targetIndustry.nameKey, { lng: 'en-US' }) ?? '',
        });
      }}
    >
      <TabPanels>
        {industries.map((industry) => {
          const theme = getTheme(industry.theme);
          const howUdacityHelpsContent = (
            <>
              <TripleLines color={theme?.backgroundAccentColor} />
              <Heading as='p' display='flex' flexDirection='column' gap='8px' position='relative'>
                <Box
                  alignItems='center'
                  as='span'
                  color={theme?.secondaryColor}
                  display='flex'
                  flex={1}
                  fontFamily='condensed'
                  fontWeight='500'
                  gap='10px'
                  letterSpacing='0.9px'
                  lineHeight='1'
                  textTransform='uppercase'
                  _after={{
                    content: '""',
                    display: 'block',
                    width: '100%',
                    height: '1px',
                    backgroundColor: theme?.secondaryColor,
                    flex: 1,
                  }}
                  fontSize={{
                    base: '0.8125rem',
                    lg: '0.9375rem',
                  }}
                >
                  {t('enterprise.overview.clientSuccess.howUdacityHelps')}
                </Box>
                <Box
                  as='span'
                  color={theme?.primaryColor}
                  display='block'
                  fontWeight='600'
                  letterSpacing='-1px'
                  lineHeight='1.15'
                  fontSize={{
                    base: '1.125rem',
                    lg: '1.5rem',
                  }}
                >
                  {t(industry.howUdacityHelps.titleKey)}
                </Box>
              </Heading>
              <List
                display='flex'
                flex='1'
                flexDirection='column'
                justifyContent='flex-start'
                position='relative'
                gap={{
                  base: '12px',
                  lg: '18px',
                }}
              >
                {industry.howUdacityHelps.reasons.map((reason) => {
                  return (
                    <ListItem
                      key={reason.textKey}
                      borderTop='1px solid'
                      borderTopColor={theme?.dividerColor}
                      alignItems={{
                        base: 'center',
                        lg: 'unset',
                      }}
                      display={{
                        base: 'flex',
                        lg: 'block',
                      }}
                      paddingTop={{
                        base: '12px',
                        lg: '18px',
                      }}
                    >
                      <ListIcon as={CheckedIcon} color={theme?.primaryColor} height='20px' width='20px' />
                      <Text
                        color='grey.700'
                        fontWeight='500'
                        lineHeight='1.4'
                        fontSize={{
                          base: '0.75rem',
                          lg: '0.8125rem',
                        }}
                      >
                        {t(reason.textKey)}
                      </Text>
                    </ListItem>
                  );
                })}
              </List>
            </>
          );

          const caseStudyButton = (
            <ButtonLink
              linkProps={{ href: industry.resourceUrl }}
              buttonProps={{
                size: 'md',
                leftIcon: <BriefCaseIcon height='14px' width='14px' />,
                width: '149px',
                fontSize: '0.75rem',
                lineHeight: '1',
                onClick: () => {
                  trackEvent({
                    type: 'click',
                    name: 'Enterprise CTA Clicked',
                    label: 'client_success_casestudy_clicked',
                    value: (t(industry.nameKey, { lng: 'en-US' }) as string) ?? '',
                  });
                },
              }}
            >
              {t('enterprise.overview.clientSuccess.viewCaseStudy')}
            </ButtonLink>
          );

          const companyLogo = (
            <>
              {industry.company?.logo && (
                <SanityImage
                  image={industry.company.logo}
                  style={{
                    objectFit: 'contain',
                    objectPosition: 'left',
                    maxWidth: '108px',
                  }}
                  fill
                />
              )}
            </>
          );

          const quoteContent = (
            <Box as='blockquote'>
              <Text
                fontSize='0.8125rem'
                fontStyle='italic'
                lineHeight='1.4'
                _after={{
                  content: "'\\201D'",
                }}
                _before={{
                  content: "'\\201C'",
                }}
              >
                {t(industry.person?.quoteKey ?? '')}
              </Text>
            </Box>
          );

          const quoteAuthorContent = (
            <>
              <Heading as='p' fontSize='1.125rem' fontWeight='600' lineHeight='1.1' mb={1}>
                {industry.person?.nameKey ? t(industry.person.nameKey) : industry.person?.name}
              </Heading>
              <Text color={theme?.primaryColor} fontSize='0.875rem' fontWeight='400' lineHeight='1.4'>
                {industry.person?.titleKey ? t(industry.person.titleKey) : industry.person?.title}
              </Text>
            </>
          );

          const renderQuoteAuthorImage = (genericIconSize = '64px') => (
            <>
              {industry.person?.useGenericIcon ? (
                <GenericPersonAvatar iconSize={genericIconSize} />
              ) : (
                industry.person?.image && (
                  <SanityImage image={industry.person.image} sizes='149px' style={{ objectFit: 'cover' }} fill />
                )
              )}
            </>
          );

          return (
            <TabPanel key={industry.id} padding='unset'>
              {/* Desktop Layout Start */}
              <Card
                border='1px solid'
                borderColor='HSLA(230, 6.5%, 82%, 0.48)'
                borderRadius='8px'
                boxShadow='none'
                flexDirection='row'
                minHeight='459px'
                overflow='hidden'
                display={{
                  base: 'none',
                  lg: 'flex',
                }}
              >
                <Grid flex='1' gridAutoRows='auto' gridTemplateColumns='194px 1fr'>
                  <Grid
                    gap='32px'
                    gridColumn='1 / -1'
                    gridRow='1 / 2'
                    gridTemplateColumns='subgrid'
                    padding='40px 28px'
                  >
                    <Flex flexDirection='column' gap='12px' gridColumn='1 / 2'>
                      <Heading
                        alignItems='center'
                        as='p'
                        bgGradient={theme?.statbgGradient}
                        borderRadius='4px'
                        color='white'
                        display='flex'
                        flexDirection='column'
                        fontSize='2.75rem'
                        fontWeight='600'
                        gap='12px'
                        letterSpacing='-2px'
                        lineHeight='0.9'
                        padding='12px'
                        whiteSpace='nowrap'
                      >
                        {industry.stat.value}
                        <Box
                          alignSelf='stretch'
                          as='span'
                          borderTop='1px solid'
                          borderTopColor='HSLA(0, 0%, 100%, 0.16)'
                          display='block'
                          fontFamily='body'
                          fontSize='0.8125rem'
                          fontWeight='400'
                          letterSpacing='normal'
                          lineHeight='1.4'
                          paddingTop='8px'
                          textAlign='center'
                        >
                          {t(industry.stat.labelKey)}
                        </Box>
                      </Heading>
                      {caseStudyButton}
                    </Flex>
                    <Flex flex='1' flexDirection='column' gap='8px'>
                      <Box _empty={{ display: 'none' }} height='32px' position='relative'>
                        {companyLogo}
                      </Box>
                      <Heading as='p' fontSize='1.5rem' fontWeight='600' lineHeight='1.14'>
                        {t(industry.titleKey)}
                      </Heading>
                      <Text>{t(industry.descriptionKey)}</Text>
                    </Flex>
                  </Grid>
                  <Grid
                    bgColor='grey.50'
                    borderTop='1px solid'
                    borderTopColor='HSLA(230, 6.5%, 82%, 0.4)'
                    gap='32px'
                    gridColumn='1 / -1'
                    gridRow='2 / 3'
                    gridTemplateColumns='subgrid'
                    padding='40px 28px'
                    position='relative'
                    _after={{
                      content: '""',
                      display: 'block',
                      '--b': '1px',
                      width: '16px',
                      aspectRatio: '1/cos(30deg)',
                      clipPath:
                        'polygon(50% var(--b), calc(var(--b)*cos(30deg)) calc(100% - var(--b)/2), calc(100% - var(--b)*cos(30deg)) calc(100% - var(--b)/2))',
                      background: 'grey.50',
                      position: 'absolute',
                      top: '-13px',
                      left: '96px',
                    }}
                    _before={{
                      content: '""',
                      display: 'block',
                      width: '16px',
                      aspectRatio: '1/cos(30deg)',
                      clipPath: 'polygon(50% 0, 100% 100%, 0 100%)',
                      background: 'hsla(230, 6.5%, 82%, 0.4)',
                      position: 'absolute',
                      top: '-14px',
                      left: '96px',
                    }}
                  >
                    <Box flexShrink={0} gridColumn='1 / 2'>
                      <Box borderRadius='4px' height='149px' overflow='hidden' position='relative' width='149px'>
                        {renderQuoteAuthorImage()}
                      </Box>
                    </Box>
                    <Flex flexDirection='column' gap='12px' justifyContent='space-between'>
                      {quoteContent}
                      <Box>{quoteAuthorContent}</Box>
                    </Flex>
                  </Grid>
                </Grid>
                <Box
                  as='aside'
                  bgColor={theme?.bgColor}
                  borderLeft='2px solid'
                  borderLeftColor={theme?.primaryColor}
                  display='flex'
                  flex='0 0 296px'
                  flexDirection='column'
                  gap='24px'
                  isolation='isolate'
                  padding='40px 28px'
                  position='relative'
                >
                  {howUdacityHelpsContent}
                </Box>
              </Card>
              {/* Desktop Layout End */}

              {/* Mobile Layout Start */}
              <Flex
                flexDirection='column'
                gap='24px'
                display={{
                  base: 'flex',
                  lg: 'none',
                }}
              >
                <Card
                  as='aside'
                  bgColor={theme?.bgColor}
                  border='1px solid'
                  borderColor='HSLA(230, 6.5%, 82%, 0.48)'
                  borderRadius='8px'
                  boxShadow='none'
                  display='flex'
                  flexDirection='column'
                  gap='12px'
                  isolation='isolate'
                  padding='24px'
                  position='relative'
                >
                  {howUdacityHelpsContent}
                </Card>
                <Card
                  border='1px solid'
                  borderColor='HSLA(230, 6.5%, 82%, 0.48)'
                  borderRadius='8px'
                  boxShadow='none'
                  overflow='hidden'
                >
                  <Flex flexDirection='column' gap='12px' padding='24px'>
                    <Flex alignItems='center' flexWrap='wrap' gap='8px' justifyContent='space-between'>
                      <Box flex='0 1 100px' height='32px' position='relative'>
                        {companyLogo}
                      </Box>
                      <Box>
                        <Tag
                          bg={theme?.bgColor}
                          border='1.5px solid'
                          borderColor={theme?.primaryColor}
                          borderRadius='4px'
                          color={theme?.primaryColor}
                          fontFamily='condensed'
                          fontSize='0.9375rem'
                          fontWeight='500'
                          letterSpacing='0.8px'
                          lineHeight='1'
                          textTransform='uppercase'
                          whiteSpace='nowrap'
                        >
                          {industry.stat.value} · {t(industry.stat.labelKey)}
                        </Tag>
                      </Box>
                    </Flex>
                    <Heading as='p' fontSize='1.5rem' fontWeight='600' lineHeight='1.14'>
                      {t(industry.titleKey)}
                    </Heading>
                    <Text>{t(industry.descriptionKey)}</Text>
                    {caseStudyButton}
                  </Flex>
                  <Box
                    bgColor='grey.50'
                    borderTop='1px solid'
                    borderTopColor='HSLA(230, 6.5%, 82%, 0.4)'
                    display='flex'
                    flexDirection='column'
                    gap='12px'
                    padding='24px'
                    position='relative'
                    _after={{
                      content: '""',
                      display: 'block',
                      '--b': '1px',
                      width: '16px',
                      aspectRatio: '1/cos(30deg)',
                      clipPath:
                        'polygon(50% var(--b), calc(var(--b)*cos(30deg)) calc(100% - var(--b)/2), calc(100% - var(--b)*cos(30deg)) calc(100% - var(--b)/2))',
                      background: 'grey.50',
                      position: 'absolute',
                      top: '-13px',
                      left: '90px',
                    }}
                    _before={{
                      content: '""',
                      display: 'block',
                      width: '16px',
                      aspectRatio: '1/cos(30deg)',
                      clipPath: 'polygon(50% 0, 100% 100%, 0 100%)',
                      background: 'hsla(230, 6.5%, 82%, 0.4)',
                      position: 'absolute',
                      top: '-14px',
                      left: '90px',
                    }}
                  >
                    {quoteContent}
                    <Flex alignItems='center' gap='12px'>
                      <Box
                        borderRadius='4px'
                        flexShrink={0}
                        height='48px'
                        overflow='hidden'
                        position='relative'
                        width='48px'
                      >
                        {renderQuoteAuthorImage('28px')}
                      </Box>
                      <Box>{quoteAuthorContent}</Box>
                    </Flex>
                  </Box>
                </Card>
              </Flex>
              {/* Mobile Layout End */}
            </TabPanel>
          );
        })}
      </TabPanels>
    </TabbedSection>
  );
}

function TripleLines({ color }: { color?: string }) {
  return (
    <Box
      as='svg'
      bottom='0px'
      color={color}
      height='175'
      pointerEvents='none'
      position='absolute'
      right='0px'
      viewBox='0 0 175 175'
      width='175'
    >
      <g opacity='0.08'>
        <rect fill='currentColor' height='247' transform='rotate(45 174.656 0)' width='20' x='174.656' />
        <rect
          fill='currentColor'
          height='247'
          transform='rotate(45 202.941 28.2842)'
          width='20'
          x='202.941'
          y='28.2842'
        />
        <rect
          fill='currentColor'
          height='247'
          transform='rotate(45 231.227 56.5686)'
          width='20'
          x='231.227'
          y='56.5686'
        />
      </g>
    </Box>
  );
}

export async function transformStaticClientSuccessStoriesSectionProps(): Promise<ClientSuccessStoriesSectionProps> {
  const { getCompany } = await import('~/features/companies/controllers/get-company');
  const { getPerson } = await import('~/features/person/controllers/get-person');

  return {
    titleKey: 'enterprise.overview.clientSuccess.title',
    subtitleKey: 'enterprise.overview.clientSuccess.subtitle',
    descriptionKey: 'enterprise.overview.clientSuccess.description',
    industries: [
      {
        id: 'products-and-manufacturing',
        nameKey: 'enterprise.overview.clientSuccess.industries.productsAndManufacturing',
        theme: 'cyan',
        resourceUrl: '/enterprise/case-study/global-tech-and-eng-case-study',
        stat: { value: '200%', labelKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.statLabel' },
        titleKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.title',
        descriptionKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.description',
        company: null,
        person: {
          nameKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.attribution.name',
          titleKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.attribution.role',
          useGenericIcon: true,
          quoteKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.quote',
        },
        howUdacityHelps: {
          titleKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.howUdacityHelpsTitle',
          reasons: [
            {
              iconId: 'gear',
              textKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.reasons.aiOperations',
            },
            {
              iconId: 'dollar-sign',
              textKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.reasons.predictiveData',
            },
            {
              iconId: 'clipboard-checklist',
              textKey: 'enterprise.overview.clientSuccess.productsAndManufacturing.reasons.smartFactories',
            },
          ],
        },
      },
      {
        id: 'technology',
        nameKey: 'enterprise.overview.clientSuccess.industries.technology',
        theme: 'blue',
        resourceUrl: '/enterprise/case-study/elm',
        stat: { value: '383%', labelKey: 'enterprise.overview.clientSuccess.technology.statLabel' },
        titleKey: 'enterprise.overview.clientSuccess.technology.title',
        descriptionKey: 'enterprise.overview.clientSuccess.technology.description',
        company: await getCompany('Elm').then((company) => {
          if (!company) return null;
          return {
            name: company.name,
            logo: company.lofiLogoDark,
          };
        }),
        person: await getPerson('Elm Executive Sponsor').then((person) => {
          if (!person) return null;
          return {
            name: person.name,
            title: person.title,
            useGenericIcon: true,
            quoteKey: 'enterprise.overview.clientSuccess.technology.quote',
          };
        }),
        howUdacityHelps: {
          titleKey: 'enterprise.overview.clientSuccess.technology.howUdacityHelpsTitle',
          reasons: [
            {
              iconId: 'gear',
              textKey: 'enterprise.overview.clientSuccess.technology.reasons.aiEnabledDigitalTransformation',
            },
            {
              iconId: 'dollar-sign',
              textKey: 'enterprise.overview.clientSuccess.technology.reasons.dataDrivenDecisionMaking',
            },
            {
              iconId: 'clipboard-checklist',
              textKey: 'enterprise.overview.clientSuccess.technology.reasons.scalableWorkforceUpskilling',
            },
          ],
        },
      },
      {
        id: 'education',
        nameKey: 'enterprise.overview.clientSuccess.industries.education',
        theme: 'purple',
        resourceUrl: '/enterprise/case-study/wgu',
        stat: { value: '500+', labelKey: 'enterprise.overview.clientSuccess.education.statLabel' },
        titleKey: 'enterprise.overview.clientSuccess.education.title',
        descriptionKey: 'enterprise.overview.clientSuccess.education.description',
        company: await getCompany('WGU').then((company) => {
          if (!company) return null;
          return {
            name: company.name,
            logo: company.lofiLogoDark,
          };
        }),
        person: await getPerson('Eric Lagally').then((person) => {
          if (!person) return null;
          return {
            name: person.name,
            title: person.title,
            image: person.image,
            quoteKey: 'enterprise.overview.clientSuccess.education.quote',
          };
        }),
        howUdacityHelps: {
          titleKey: 'enterprise.overview.clientSuccess.education.howUdacityHelpsTitle',
          reasons: [
            {
              iconId: 'gear',
              textKey: 'enterprise.overview.clientSuccess.education.reasons.industryAlignedCurriculum',
            },
            {
              iconId: 'dollar-sign',
              textKey: 'enterprise.overview.clientSuccess.education.reasons.realWorldProjectBased',
            },
            {
              iconId: 'clipboard-checklist',
              textKey: 'enterprise.overview.clientSuccess.education.reasons.workforceReadySkills',
            },
          ],
        },
      },
      {
        id: 'resources-and-energy',
        nameKey: 'enterprise.overview.clientSuccess.industries.resourcesAndEnergy',
        theme: 'blue',
        resourceUrl: '/enterprise/case-study/energy-case-study',
        stat: { value: '2.5', labelKey: 'enterprise.overview.clientSuccess.resourcesAndEnergy.statLabel' },
        titleKey: 'enterprise.overview.clientSuccess.resourcesAndEnergy.title',
        descriptionKey: 'enterprise.overview.clientSuccess.resourcesAndEnergy.description',
        company: null,
        person: await getPerson('Data Engineering Executive').then((person) => {
          if (!person) return null;
          return {
            name: person.name,
            title: person.title,
            useGenericIcon: true,
            quoteKey: 'enterprise.overview.clientSuccess.resourcesAndEnergy.quote',
          };
        }),
        howUdacityHelps: {
          titleKey: 'enterprise.overview.clientSuccess.resourcesAndEnergy.howUdacityHelpsTitle',
          reasons: [
            {
              iconId: 'gear',
              textKey: 'enterprise.overview.clientSuccess.resourcesAndEnergy.reasons.energyProduction',
            },
            {
              iconId: 'dollar-sign',
              textKey: 'enterprise.overview.clientSuccess.resourcesAndEnergy.reasons.networkData',
            },
            {
              iconId: 'clipboard-checklist',
              textKey: 'enterprise.overview.clientSuccess.resourcesAndEnergy.reasons.distributedAssets',
            },
          ],
        },
      },
    ],
  };
}
